// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** All known main dashboard widget IDs. */
export const DASHBOARD_WIDGET_IDS = [
    // Sprint 61.0.10 — the page's top band. Each KPI tile gates on its own
    // module inside the strip, so the strip itself carries no dependency.
    'kpi_strip',
    'compliance_score',
    // Sprint 61.0.10 — prioritised recommendations and per-framework requirement
    // coverage, both fed by `GET /dashboard/summary` (B0 contract).
    'recommendations',
    'framework_coverage',
    // Sprint 63.13.0 — the share of pack-declared applicable controls with
    // recorded automated work. Its KPI tile borrows this widget's presence, and
    // the panel names every bucket the figure was taken over.
    'automation_impact',
    'rights_requests',
    'incidents',
    'drift_approvals',
    'monitoring',
    'coverage',
    'task_summary',
    'upcoming_tasks',
    'overdue_alerts',
    // Sprint 61.0.10 — renamed from `notifications`; the retired id is read as
    // this one through `LEGACY_WIDGET_ID_ALIASES` / `normalizeWidgetId`.
    'recent_activity',
    'filing_lifecycle',
    'department_consolidation',
    'vendor_risk',
    'discovery_classification',
    'ai_governance',
    'breach_response',
    'audit_readiness',
    'my_tasks',
    'my_section_status',
    'revision_requests',
    // Sprint 43.5.6 Epic B — new Light-tier widgets. Fill the dashboard
    // real-estate freed by the Standard/Enterprise widgets hidden on
    // Light, so a Light-package demo still feels content-rich.
    'consent_summary',
    'transfer_assessments',
    'breach_notifications',
    'authority_requests',
    'data_subjects',
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
    'audit_activity_summary',
    'onboarding_progress',
    // Sprint 46.2.0 — Sector Overlay Reports Due. Self-hides when no
    // direct-mode sector overlays are installed; surfaces on banking/etc.
    // tenants only.
    'sector_overlay_reports_due',
    // Sprint 61.0.10 — installed-module status per sidebar category. Always
    // available: it reads the org's own module configuration.
    'module_status',
];
/**
 * Default column span for each widget, in TRACKS.
 *
 * Sprint 61.0.10.4 AC1 — the grid is no longer two columns. It flows
 * (`auto-fit`, `minmax(22rem, 1fr)`), so it is two tracks at 1280 px as before
 * and four at 1920 px, and a `2` here means "two tracks", not "the whole row".
 * `auto-fit` collapses a track no item occupies and the row's items share the
 * width, so a section whose only widget spans two tracks still fills the row.
 */
export const WIDGET_DEFAULT_COL_SPAN = {
    kpi_strip: 2,
    compliance_score: 1,
    recommendations: 1,
    /*
     * Sprint 61.0.10.2 AC6 — full width.
     *
     * At `1` it drew its unbounded framework list down a half-width column with
     * twelve printed pages of empty column beside it. Now that the list is
     * capped at eight ranked rows the card is short, and a bar chart is read
     * across, not down: a full-width row gives each bar the length that makes
     * the ranking legible.
     */
    framework_coverage: 2,
    // Sprint 63.13.0 — one headline and four counts; width is earned by content.
    automation_impact: 1,
    /*
     * Sprint 61.0.10.4 AC2 — back to one track.
     *
     * It was widened to 2 by 61.0.10.2's hole-filling pass. The widget renders
     * three integers and one bar; at 1920 px two tracks made that ~1558 px of
     * card, which is the single emptiest thing in the product owner's review.
     * Width is earned by content, and this content does not earn it.
     */
    rights_requests: 1,
    incidents: 1,
    drift_approvals: 1,
    monitoring: 1,
    coverage: 1,
    task_summary: 1,
    upcoming_tasks: 1,
    overdue_alerts: 1,
    recent_activity: 1,
    filing_lifecycle: 1,
    department_consolidation: 1,
    vendor_risk: 1,
    discovery_classification: 1,
    ai_governance: 1,
    breach_response: 1,
    audit_readiness: 1,
    my_tasks: 1,
    my_section_status: 1,
    revision_requests: 1,
    // Sprint 43.5.6 Epic B — new Light-tier widgets (all single-col).
    consent_summary: 1,
    transfer_assessments: 1,
    breach_notifications: 1,
    authority_requests: 1,
    data_subjects: 1,
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING Light-tier widgets.
    audit_activity_summary: 1,
    onboarding_progress: 1,
    sector_overlay_reports_due: 2,
    module_status: 2,
};
/** Maps each widget to its parent section. */
export const WIDGET_SECTION_MAP = {
    kpi_strip: 'overview',
    compliance_score: 'core_metrics',
    recommendations: 'core_metrics',
    framework_coverage: 'core_metrics',
    automation_impact: 'core_metrics',
    rights_requests: 'core_metrics',
    incidents: 'core_metrics',
    drift_approvals: 'core_metrics',
    monitoring: 'core_metrics',
    coverage: 'core_metrics',
    task_summary: 'task_management',
    upcoming_tasks: 'task_management',
    overdue_alerts: 'task_management',
    recent_activity: 'task_management',
    my_tasks: 'task_management',
    my_section_status: 'task_management',
    revision_requests: 'task_management',
    filing_lifecycle: 'compliance_insights',
    department_consolidation: 'compliance_insights',
    vendor_risk: 'compliance_insights',
    discovery_classification: 'compliance_insights',
    ai_governance: 'compliance_insights',
    breach_response: 'compliance_insights',
    audit_readiness: 'compliance_insights',
    // Sprint 43.5.6 Epic B — new Light-tier widgets.
    consent_summary: 'core_metrics',
    data_subjects: 'core_metrics',
    transfer_assessments: 'compliance_insights',
    breach_notifications: 'compliance_insights',
    authority_requests: 'compliance_insights',
    // Sprint 45.1.0 — AUDIT_TRAIL is an insight; ONBOARDING is a task-management nudge.
    audit_activity_summary: 'compliance_insights',
    onboarding_progress: 'task_management',
    sector_overlay_reports_due: 'compliance_insights',
    module_status: 'compliance_insights',
};
/**
 * Sprint 43.5.5 — Module dependencies for each dashboard widget.
 * Sprint 43.5.6 — refined to hide-vs-degrade split. Widgets listed with
 * an empty array always render ("degrade gracefully" on Light-tier data
 * from whatever modules ARE installed). Widgets listed with one or more
 * modules hide entirely if ANY of those modules is disabled.
 *
 * Used by `DashboardPage` to hide widgets whose underlying module is not
 * enabled for the org's package tier. Required so a Light-package demo
 * doesn't show widgets for modules that aren't installed (e.g. Compliance
 * Score is a Governance feature and disappears from the grid when
 * Governance is disabled).
 *
 * Module IDs are intentionally typed as `string` (not `ModuleId`) to
 * avoid a type-level circular import between `dashboard.ts` and
 * `module-config.ts`. The values are verified against `ModuleId` at
 * runtime by the gating hook.
 */
export const WIDGET_MODULE_DEPENDENCIES = {
    // Overview — Sprint 61.0.10. The strip always renders; each tile inside it
    // gates on its own module, so a Light tenant sees the four Light tiles.
    kpi_strip: [],
    // Core metrics
    compliance_score: ['governance'], // the Compliance Score product IS Governance
    recommendations: ['governance'], // derived from the compliance score's factors
    framework_coverage: ['localization'], // installed regulatory frameworks live in Localization
    // Sprint 63.13.0 — reads the applicability the installed regulatory
    // frameworks declare, which live in Localization, like `framework_coverage`.
    automation_impact: ['localization'],
    rights_requests: ['rights_requests'],
    incidents: ['incidents'],
    drift_approvals: ['governance'],
    monitoring: ['monitoring'],
    coverage: [], // DEGRADE: shows whatever Light modules contribute
    // Task management
    // Sprint 45.2.0 — task_summary/upcoming_tasks/my_tasks flipped from
    // DEGRADE to HIDE. The widgets' "View all tasks" CTAs route into
    // /admin/tasks/*, which is RBAC-gated on Task Management permissions.
    // On Light (Task Management is Standard+), the widgets rendered
    // happily but every click-through hit 403. Gating on task_management
    // removes the broken CTAs from the Light dashboard entirely.
    task_summary: ['task_management'],
    upcoming_tasks: ['task_management'],
    overdue_alerts: ['task_management'], // HIDE: per product, overdue alerts only source from Task Management
    recent_activity: [], // core notification system; no module gate (licenses, MFA, SSO — org-wide infrastructure)
    my_tasks: ['task_management'],
    my_section_status: ['program_governance'],
    revision_requests: ['program_governance'],
    // Compliance insights
    filing_lifecycle: ['assessments'],
    department_consolidation: ['program_governance'],
    vendor_risk: ['vendor_risk'],
    discovery_classification: ['discovery', 'classification'],
    ai_governance: ['classification', 'discovery'],
    breach_response: ['incidents'], // degrades on Light; incident_ops is enrichment only
    audit_readiness: ['assessments'], // degrades on Light; program_governance is enrichment only
    // Sprint 43.5.6 Epic B — new Light-tier widgets.
    consent_summary: ['consent'],
    transfer_assessments: ['assessments'],
    breach_notifications: ['incidents'],
    authority_requests: ['compliance_forms'],
    data_subjects: ['data_subjects'],
    // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING widget module gates.
    audit_activity_summary: ['audit_trail'],
    onboarding_progress: [], // DEGRADE: wizard state is org-level, always available
    // Widget self-hides when no direct-mode sector overlays are installed
    // (data-state gate, stronger than module-availability), so no module dep.
    sector_overlay_reports_due: [],
    // Sprint 61.0.10 — installed-module status reads the org's module config,
    // which every tier has.
    module_status: [],
};
/* ── Sprint 61.0.10: retired widget ids ── */
/**
 * Retired widget ids and the id each is read as today.
 *
 * ONE table, consumed by both stores: `useDashboardConfig` (localStorage and
 * API payloads) on the frontend and `DashboardPreferencesService` / the
 * `PUT /dashboard/preferences` validator on the backend. A stored preference or
 * `widget_order` naming a retired id is normalised on read and on write; nothing
 * is dropped. Add a row here when an id is renamed — never a second map.
 */
export const LEGACY_WIDGET_ID_ALIASES = {
    notifications: 'recent_activity',
};
const CURRENT_WIDGET_IDS = new Set(DASHBOARD_WIDGET_IDS);
/**
 * Resolve any stored widget id to a current one.
 *
 * Returns the id itself when it is current, the successor when it is a retired
 * alias, and `null` when it is neither — the caller decides whether `null` is
 * dropped (a read) or rejected (a write).
 */
export function normalizeWidgetId(id) {
    if (CURRENT_WIDGET_IDS.has(id))
        return id;
    // Own-property lookup only. A plain `LEGACY_WIDGET_ID_ALIASES[id]` answers
    // `constructor`, `__proto__` and `toString` with `Object.prototype` members,
    // which a `PUT /preferences` body can name as a widget key (E12.5 security
    // pass, Sprint 61.0.10).
    if (!Object.hasOwn(LEGACY_WIDGET_ID_ALIASES, id))
        return null;
    return LEGACY_WIDGET_ID_ALIASES[id];
}
/**
 * Which dashboard sections start collapsed for a user who has set no preference.
 *
 * ## Why this is shared, and why it exists at all
 *
 * Sprint 61.0.10.2 AC8: all four sections opened by default, so a first load
 * rendered roughly thirty widgets expanded and the dashboard printed to 23
 * pages. The default page is now the headline strip plus the core panels;
 * Task Management and Compliance Insights are one click from open.
 *
 * It is **one constant read by both sides** because the first cut of this
 * change was not, and was therefore inert. The frontend's
 * `getDefaultSections()` and the backend's `readSections(undefined)` are two
 * spellings of one policy, and the backend answers `GET
 * /dashboard/preferences` for every user — so a client-side default is
 * overwritten by the server's before it is ever rendered. Changing one and not
 * the other produced a default nobody could receive, and the unit test over
 * the hook passed because it mounts without an API. Found by rendering the
 * page, not by reading the diff.
 *
 * A DEFAULT, not a policy: a stored `false` on either side still wins.
 */
export const DEFAULT_COLLAPSED_SECTIONS = {
    overview: false,
    core_metrics: false,
    task_management: true,
    compliance_insights: true,
};
/** Dashboard presentation choices, shared by the preference boundary and UI. */
export const DASHBOARD_DENSITIES = ['comfortable', 'compact'];
export const DASHBOARD_TAB_IDS = [
    'core_metrics',
    'task_management',
    'compliance_insights',
];
/* ── Sprint 22.7.2: Drag-and-Drop Widget Reordering ── */
/**
 * Returns the default widget order for a given section based on DASHBOARD_WIDGET_IDS.
 */
export function getDefaultSectionOrder(sectionId) {
    return DASHBOARD_WIDGET_IDS.filter((id) => WIDGET_SECTION_MAP[id] === sectionId);
}
/**
 * Default widget configurations per persona.
 * First-visit users see only the widgets relevant to their detected persona.
 * If no persona detected, falls back to all-visible (existing behavior).
 */
// `Partial` is deliberate. This table holds entries for the 16 `P-VN-*` personas
// only, while the registry now has 35 — `useDashboardConfig` falls through to
// all-visible defaults for the other 19. `Record` asserted total coverage that
// does not exist; `Partial` makes the gap explicit, and
// persona-widget-defaults.test.ts pins exactly which ids are allowed to be absent.
export const PERSONA_WIDGET_DEFAULTS = {
    // P-VN-01: Enterprise DPO — compliance, rights, incidents, filing, audit
    'P-VN-01': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'incidents',
            'drift_approvals',
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'data_subjects',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
            'sector_overlay_reports_due',
            'module_status',
        ],
        collapsed: ['coverage', 'monitoring'],
    },
    // P-VN-02: SME Privacy Officer — streamlined compliance focus
    'P-VN-02': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'incidents',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'recent_activity',
            'filing_lifecycle',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'data_subjects',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
            // Sprint 45.1.0 — AUDIT_TRAIL + ONBOARDING widgets for the Light
            // persona so a fresh install surfaces what's configured so far and
            // nudges completion of the setup wizard.
            'audit_activity_summary',
            'onboarding_progress',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-03: Compliance Program Manager — program governance, monitoring
    'P-VN-03': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'department_consolidation',
            'audit_readiness',
            'my_tasks',
            // Sprint 43.5.6 Epic B — new Light-tier widgets
            'consent_summary',
            'transfer_assessments',
            'breach_notifications',
            'authority_requests',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-04: Department Data Owner — department tasks, section status
    'P-VN-04': {
        visible: [
            'kpi_strip',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'recent_activity',
            'my_tasks',
            'my_section_status',
            'revision_requests',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-05: Data Governance Lead — discovery, classification, governance
    'P-VN-05': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'discovery_classification',
            'vendor_risk',
            'department_consolidation',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-06: Security Operations Lead — incidents, breach, drift
    'P-VN-06': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'incidents',
            'drift_approvals',
            'monitoring',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'breach_response',
            'my_tasks',
            'sector_overlay_reports_due',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-07: IT Systems Administrator — coverage, connectors, monitoring
    'P-VN-07': {
        visible: [
            'kpi_strip',
            'monitoring',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'recent_activity',
            'discovery_classification',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-08: Legal Counsel — compliance, filings, AI governance
    'P-VN-08': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'incidents',
            'task_summary',
            'upcoming_tasks',
            'filing_lifecycle',
            'ai_governance',
            'audit_readiness',
            'my_tasks',
            'sector_overlay_reports_due',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-09: Breach Response Lead — incident + breach workflows (Sprint 43.5.0 E10 — persona label fix)
    // Previously miscommented as "Executive / Board Member"; persona registry
    // `docs/content/personas-and-usecases.md` defines P-VN-09 as Breach Response Lead.
    // Widget bundle revised to surface the queues this persona works from daily.
    'P-VN-09': {
        visible: [
            'kpi_strip',
            'incidents',
            'breach_response',
            'drift_approvals',
            'filing_lifecycle',
            'audit_readiness',
            'task_summary',
            'overdue_alerts',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-10: Internal Audit Lead — audit, evidence, compliance
    'P-VN-10': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'department_consolidation',
            'my_tasks',
            'sector_overlay_reports_due',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-11: HR & Employee Data Steward — consent, rights, tasks
    'P-VN-11': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'recent_activity',
            'coverage',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-12: Multinational Subsidiary Compliance Lead — assessments, rights, localization
    'P-VN-12': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'incidents',
            'drift_approvals',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
            'module_status',
        ],
        collapsed: ['drift_approvals'],
    },
    // P-VN-13: Education Compliance Lead — consent, rights, monitoring
    'P-VN-13': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'rights_requests',
            'monitoring',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'coverage',
            'recent_activity',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-14: External Legal Advisor — multi-client portfolio, filing, audit
    'P-VN-14': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'filing_lifecycle',
            'audit_readiness',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-15: Law Firm Operations Manager — tasks, deliverables, deadlines
    'P-VN-15': {
        visible: [
            'kpi_strip',
            'task_summary',
            'upcoming_tasks',
            'overdue_alerts',
            'recent_activity',
            'my_tasks',
            'module_status',
        ],
        collapsed: [],
    },
    // P-VN-16: Business Owner / CEO — executive dashboard (Sprint 43.5.0 E10 — bundle expansion)
    // Extended from 5 to 8 widgets so Scene 7 of the VN Decree 356 demo close
    // renders the full compliance picture (filing completeness, active rights
    // requests, incident posture) that a CEO/board audience expects.
    'P-VN-16': {
        visible: [
            'kpi_strip',
            'compliance_score',
            'recommendations',
            'framework_coverage',
            'filing_lifecycle',
            'incidents',
            'rights_requests',
            'audit_readiness',
            'monitoring',
            'overdue_alerts',
            'recent_activity',
            'module_status',
        ],
        collapsed: ['monitoring'],
    },
};
/**
 * The scoring service's five recommendation sentences, keyed, with the factor
 * each addresses and the admin route that acts on it.
 *
 * R-46 is not engaged here: every `route` is a platform route and every
 * `module` a platform module id — no instrument, authority, form or
 * country-specific vocabulary. The routes are asserted against the route table
 * in `App.tsx` by a frontend test (Sprint 61.0.10 AC4), not by a hand list.
 *
 * `text` is matched verbatim against `grc_compliance_scores.recommendations[]`
 * (written by `governance/scoring-service.ts`); a sentence not found here is
 * still returned by the summary with `key: null` and its own text, so a new
 * recommendation is never silently dropped.
 */
export const DASHBOARD_RECOMMENDATION_CATALOG = {
    rec_improve_system_gov: {
        text: 'Improve system governance documentation',
        factor_key: 'factor_avg_system_score',
        route: '/admin/governance/scores',
        module_id: 'governance',
    },
    rec_improve_vendor_gov: {
        text: 'Improve vendor governance documentation',
        factor_key: 'factor_avg_vendor_score',
        route: '/admin/governance/vendors',
        module_id: 'governance',
    },
    rec_document_processing: {
        text: 'Document processing activities for uncovered systems',
        factor_key: 'factor_processing_coverage',
        route: '/admin/data-mapping/activities',
        module_id: 'data_mapping',
    },
    rec_generate_ropa: {
        text: 'Generate RoPA records for active processing activities',
        factor_key: 'factor_ropa_completeness',
        route: '/admin/data-mapping/ropa-completeness',
        module_id: 'data_mapping',
    },
    rec_resolve_drift: {
        text: 'Resolve open critical and high severity drift events',
        factor_key: 'factor_drift_penalty',
        route: '/admin/governance/drift',
        module_id: 'governance',
    },
};
/**
 * Scoring-service factor `name` → stable factor key. The scoring service writes
 * its factor names as English literals; this is the one place they are mapped,
 * so the priority derivation and the frontend labels share a vocabulary.
 */
export const DASHBOARD_FACTOR_KEYS = {
    'Average system score': 'factor_avg_system_score',
    'Average vendor score': 'factor_avg_vendor_score',
    'Processing activity coverage': 'factor_processing_coverage',
    'RoPA completeness': 'factor_ropa_completeness',
    'Drift events penalty': 'factor_drift_penalty',
};
/**
 * Display rank of a recommendation priority: most urgent first.
 *
 * Sprint 61.0.10.2 AC2. The dashboard printed **Medium, Medium, High, High**:
 * `RecommendationsWidget` took `items.slice(0, 4)` in whatever order the server
 * sent, so the four shown were neither the four most urgent nor in any order.
 *
 * One definition, two applications. The backend sorts with it in
 * `deriveRecommendations`, where it can also tie-break on how far each factor
 * fell below its weight; the widget sorts with it again before slicing, so the
 * displayed property is guaranteed by the component that displays it rather
 * than inherited from a caller. `Array.prototype.sort` is stable (ES2019), so
 * the widget's re-sort preserves the server's tie-break inside each band.
 */
export const DASHBOARD_RECOMMENDATION_PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
/** Comparator over {@link DASHBOARD_RECOMMENDATION_PRIORITY_RANK}. */
export function compareRecommendationPriority(a, b) {
    return (DASHBOARD_RECOMMENDATION_PRIORITY_RANK[a.priority] -
        DASHBOARD_RECOMMENDATION_PRIORITY_RANK[b.priority]);
}
/**
 * The audit-readiness group for records carrying no framework code.
 *
 * Sprint 61.0.10.2 AC3.4. The backend groups DPIAs and gaps by
 * `framework_code || 'unspecified'`, and the widget rendered the group key
 * itself — so five assessments appeared under a heading reading
 * **"Unspecified"**, which is a sentinel, not a name. The group is kept rather
 * than dropped (the records are real work and hiding them would be worse than
 * naming them badly), and the frontend renders a localized heading for it.
 *
 * Shared so the two sides compare against one value instead of each spelling
 * the sentinel themselves.
 */
export const AUDIT_READINESS_UNSPECIFIED_FRAMEWORK = 'unspecified';
/**
 * The collections the consent grant-rate tile may have read its figure from,
 * in the order the server tries them.
 *
 * Sprint 61.0.10.2 returned the source because the reader FALLS BACK: a hint
 * hardcoded to "web consent records" would be false for a tenant whose figure
 * came from the second or third collection. The frontend maps the value to a
 * localized noun; the collection name itself never reaches an operator.
 *
 * ## Why this lives in `shared` rather than in the service that reads it
 *
 * It was declared in `dashboard-service.ts` and referenced nowhere outside that
 * one file, so the frontend typed the field as the open `string | null` and the
 * i18n keys were selected by interpolating a server value into a key name. The
 * guard that was supposed to hold the two sides together hand-copied the three
 * values into an `it.each` table instead of deriving them — so a fourth
 * collection added here would get no key, silently fall back to the noun the
 * sprint had just retired, and leave the suite green.
 *
 * That is the hand-list defect class this repository records under R-46 and in
 * the 62.23.0 boundary-tag guard. Here the union is the single declaration,
 * both sides import it, and the test derives its table from it.
 *
 * Adding a member REQUIRES a `dashboard.kpi.consent_coverage_source_<value>`
 * key in every locale catalogue; `DashboardPage.test.tsx` asserts exactly that
 * and fails on a member without one.
 */
export const CONSENT_COVERAGE_SOURCES = [
    'web_consent_records',
    'web_consent_events',
    'consent_records',
];
/* ── Sprint 63.13.0 — the Automation Impact block of `GET /dashboard/summary` ── */
/**
 * Which of the tile's states the server is reporting — Sprint 63.13.0
 * (work file, "Design adjustments": the absence states in precedence order).
 *
 * The server evaluates them in THIS order and reports the first that holds;
 * `figure` only when none does. A failed read is not a member: the block is
 * then `{ error: true }` (`AutomationImpactLoadError`), the frontend's
 * `load_error`, following the `framework_coverage` block's contract.
 *
 * - `no_frameworks_installed` — no owed framework is installed (a framework
 *   installed in reference mode, or in `draft` or `legacy` status, is not
 *   owed). The name is older than that reading: an organisation whose only
 *   installs are reference-only reaches it, so its copy says no installed
 *   framework is one the organisation must comply with, never that nothing is
 *   installed (Gate E12.5 pass 3).
 * - `not_declared` — owed frameworks are installed and none declares
 *   applicability for any requirement.
 * - `profile_incomplete` — nothing applies (applicable count 0), and at least
 *   one declared requirement is waiting for a profile fact the organisation
 *   has not stated — whatever the out-of-scope count (Gate E12.5 pass 1,
 *   P3-3). An unresolved requirement might apply once the fact is stated. The
 *   only state that points the operator at the sectors page.
 * - `none_applicable` — nothing applies, and every declaration resolved: all
 *   declared requirements are out of scope, none waiting. NOT a statement about
 *   sectors (Gate E12.5 pass 2, P3-1): a requirement is out of scope either
 *   because its pack declares it `not_applicable` — which reads no profile at
 *   all — or because a condition over a stated attribute is false, so this can
 *   hold with no sectors stated. Its copy and link name the declarations (the
 *   frameworks page), never "your stated sectors".
 * - `no_emitter` — applicable controls exist and no emitter of control
 *   execution events is registered (owner ruling OD20: the platform records no
 *   automated work against controls yet). Never `0%`.
 * - `no_point` — everything above is present and no completed month has a
 *   figure to show: no month has closed yet (the running month's open point is
 *   never shown), or the latest CLOSED month's stored point fails a
 *   precondition of `figure` below — most often because its pack was upgraded
 *   or an emitter de-registered since it was recorded (Gate E12.5 pass 1,
 *   P3-2). A point may therefore have been recorded and still leave this state;
 *   the copy says "no completed month has a figure to show yet", never "none
 *   recorded" (Gate E12.5 pass 2, P3-2).
 * - `figure` — the value of the latest CLOSED month's stored point, labelled
 *   with that row's own `period_key`, when that point meets every
 *   precondition: it is closed (`recorded_at >= period_end`); its value and
 *   both counts are finite and the applicable count is above 0; its `sources`
 *   are non-empty and each is still owed AND installed from the same pack code
 *   and version as now; its `emitters` are non-empty and each is still
 *   registered now. An older closed month is not substituted when the latest
 *   fails one; `previous` is the closed month before it, under the same
 *   preconditions.
 */
export const AUTOMATION_IMPACT_STATES = [
    'figure',
    'no_frameworks_installed',
    'not_declared',
    'profile_incomplete',
    'none_applicable',
    'no_emitter',
    'no_point',
];
//# sourceMappingURL=dashboard.js.map